import { Alert, Button, Dropdown, Space, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdKeyboardArrowDown } from 'react-icons/md'
import { VscSend } from 'react-icons/vsc'

import { Hint } from '@/Ship/Components/Hint'
import { LETTER_PROVIDER_OPTIONS } from '@/Ship/Contracts/LetterProvider'

import { LETTER_TEXTS as T } from '../Contracts/ILetter'
import { LetterTemplatesStore } from '../Stores/LetterTemplatesStore'

import type { LetterDrawerStore } from '../Stores/LetterDrawerStore'
import type { LetterProviderValue } from '@/Ship/Contracts/LetterProvider'
import type { MenuProps } from 'antd'

export interface SendLetterButtonProps {
    /** Провайдер шаблонів: `client`, `carrier`, `client_contact`, `carrier_contact` */
    provider: LetterProviderValue
    /** uuid власника — і для набору шаблонів (`applicableTo`), і для генерації */
    ownerUuid: string
    /** Шухляда, у якій відкриється нова чернетка */
    drawer: LetterDrawerStore
    /** Перечитати вкладку, коли шухляду закриють */
    onChanged: () => void
}

/**
 * Кнопка «Відправити лист» вкладки «Листи» (`SPEC-013` §9.3; легасі
 * `AddButtonCompanyMailCrud.jsx`):
 *
 *  - поки шаблони вантажаться — крутиться;
 *  - немає шаблону за замовчуванням — кнопки немає зовсім (R-05), а на її
 *    місці — постійне пояснення `Alert`, а не підказка: підказку можна
 *    сховати, і тоді вкладка лишалася без кнопки й без пояснення (вичитка
 *    підказок 2026-10-14, №15). Шаблони не завантажилися — ні кнопки, ні
 *    пояснення;
 *  - клік — чернетка з шаблону за замовчуванням, стрілка поруч — меню
 *    «Генерація документу» з рештою шаблонів (лише коли вони є); над кнопкою —
 *    підказка `crm.letters.default-template`, лише поки кнопка є;
 *  - генерація → шухляда листа в редагуванні; помилка — «Помилка» з текстом
 *    сервера.
 *
 * ⚠️ `Dropdown.Button` в antd 6 застарів — кнопка й стрілка зібрані з
 * `Space.Compact`, як у «Друкованих формах».
 */
export const SendLetterButton = observer(({ provider, ownerUuid, drawer, onChanged }: SendLetterButtonProps) => {
    const [templates] = useState(() => new LetterTemplatesStore(provider, ownerUuid))
    // Генерація триває: кнопка крутиться й не приймає повторних кліків
    const [generating, setGenerating] = useState(false)

    useEffect(() => {
        void templates.load()
    }, [templates])

    if (!templates.isLoaded) return <Button loading aria-label={T.templatesLoading} />

    if (templates.failed) return null

    const defaultTemplate = templates.defaultTemplate

    if (!defaultTemplate) {
        const providerLabel = LETTER_PROVIDER_OPTIONS.find((option) => option.value === provider)?.label ?? provider

        return <Alert type="info" showIcon title={T.noDefaultTemplate(providerLabel)} data-testid="letters-no-default-template" />
    }

    const generate = (templateUuid: string): void => {
        if (generating) return

        setGenerating(true)
        void drawer.generate(templateUuid, ownerUuid, onChanged).finally(() => setGenerating(false))
    }

    const others = templates.otherTemplates
    const items: MenuProps['items'] =
        others.length > 0
            ? [
                  {
                      type: 'group',
                      key: 'generate',
                      label: T.menuGroup,
                      children: others.map((template) => ({ key: template.uuid, label: template.title })),
                  },
              ]
            : []

    return (
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Hint id="crm.letters.default-template">
                Кнопка «Відправити лист» відкриває чернетку за шаблоном за замовчуванням, інші шаблони — у меню стрілки
                поруч.
            </Hint>
            <Space.Compact>
                <Button type="primary" icon={<VscSend />} loading={generating} onClick={() => generate(defaultTemplate.uuid)}>
                    {T.sendLetter}
                </Button>
                {items.length > 0 ? (
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={generating}
                        menu={{ items, onClick: ({ key }) => generate(String(key)) }}
                    >
                        <Tooltip title={T.otherTemplates}>
                            <Button type="primary" icon={<MdKeyboardArrowDown />} aria-label={T.otherTemplates} />
                        </Tooltip>
                    </Dropdown>
                ) : null}
            </Space.Compact>
        </Space>
    )
})
